import { useState, useEffect, useRef, useMemo } from "react";
import { useOutletContext, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { formatCurrency } from "../lib/utils";
import { toast } from "sonner";
import {
  Package, Plus, Edit, Trash2, Search, Loader2, AlertTriangle, Camera,
  FileSpreadsheet, RefreshCcw, ArrowUpDown, LayoutDashboard, Boxes, ClipboardList, Sparkles, FileBarChart,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

// Extracted components
import { toBase, findBestMatch } from "../components/inventory/inventoryConstants";
import { LazyIngredientImage } from "../components/inventory/LazyIngredientImage";
import { IngredientDialog } from "../components/inventory/IngredientDialog";
import { PageHeader } from "../components/layout/PageHeader";
import { PageSkeleton } from "../components/layout/PageSkeleton";
import { InvoiceImportDialog } from "../components/inventory/InvoiceImportDialog";
import { CameraCaptureDialog } from "../components/inventory/CameraCaptureDialog";
import { MergeDialog } from "../components/inventory/MergeDialog";
import { CsvImportDialog } from "../components/inventory/CsvImportDialog";
import { ReconcileResultBanner } from "../components/inventory/ReconcileResultBanner";
import { InventoryDashboard } from "../components/inventory/InventoryDashboard";
import { InventoryCountView } from "../components/inventory/InventoryCountView";
import { QuickAddIngredientDialog } from "../components/inventory/QuickAddIngredientDialog";
import { WasteDialog } from "../components/inventory/WasteDialog";
import { InventoryReports } from "../components/inventory/InventoryReports";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { ChevronDown, Upload as UploadIcon } from "lucide-react";

export default function InventoryPage() {
  const { currentRestaurant } = useOutletContext();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(() => (
    typeof window !== "undefined" && new URLSearchParams(window.location.search).get("filter") === "low_stock"
      ? "productos"
      : "dashboard"
  ));
  const [dashboardKey, setDashboardKey] = useState(0); // force refresh when data mutates
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [wasteOpen, setWasteOpen] = useState(false);
  const [wastePreselectedId, setWastePreselectedId] = useState(null);
  const [ingredients, setIngredients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState(null);
  const [pendingDupInventory, setPendingDupInventory] = useState(null); // { duplicate, resolve }

  // Search and filter — `?filter=low_stock` query param activates the low-stock filter on mount
  // (deep-link from Dashboard "Alertas stock" KPI card).
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [showLowStockOnly, setShowLowStockOnly] = useState(searchParams.get("filter") === "low_stock");

  useEffect(() => {
    if (searchParams.get("filter") === "low_stock") {
      setShowLowStockOnly(true);
      // Clean the URL so a future manual reload doesn't re-force the filter
      const next = new URLSearchParams(searchParams);
      next.delete("filter");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Selection for bulk delete
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [deleting, setDeleting] = useState(false);

  // Merge
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergePair, setMergePair] = useState(null);
  const [mergeForm, setMergeForm] = useState({});
  const [mergeNormalized, setMergeNormalized] = useState(null);

  // Invoice import
  const [importingInvoice, setImportingInvoice] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemMappings, setItemMappings] = useState({});
  const [importProgress, setImportProgress] = useState("");
  const invoiceFileRef = useRef(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [reconcileData, setReconcileData] = useState(null);

  // Reconcile & CSV import
  const [reconciling, setReconciling] = useState(false);
  const [reconcileResult, setReconcileResult] = useState(null);
  const [csvImportOpen, setCsvImportOpen] = useState(false);

  const [formData, setFormData] = useState({
    name: "", unit: "kg", cost_per_unit: "", waste_percentage: "0",
    supplier_id: "", min_stock: "0", current_stock: "0", image_url: ""
  });

  useEffect(() => {
    if (!currentRestaurant) return;
    fetchData();
  }, [currentRestaurant]); // eslint-disable-line

  const fetchData = async () => {
    try {
      const [ingredientsData, suppliersData] = await Promise.all([
        api.getIngredients(currentRestaurant.restaurant_id),
        api.getSuppliers(currentRestaurant.restaurant_id)
      ]);
      setIngredients(ingredientsData);
      setSuppliers(suppliersData);
      setDashboardKey(k => k + 1);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  // ─── Handlers ─────────────────────────────────────────────────────

  const handleReconcile = async () => {
    setReconciling(true); setReconcileResult(null);
    try {
      const result = await api.reconcileInventory(currentRestaurant.restaurant_id);
      setReconcileResult(result);
      if (result.created_count > 0) {
        toast.success(`${result.created_count} ingrediente${result.created_count > 1 ? "s" : ""} creado${result.created_count > 1 ? "s" : ""}`);
        await fetchData();
      } else toast.info("Inventario sincronizado — no faltan ingredientes");
    } catch (err) { toast.error(err.message || "Error al sincronizar con recetas"); }
    finally { setReconciling(false); }
  };

  const handleDeleteSelected = async () => {
    if (selectedIngredients.length === 0) return;
    if (!window.confirm(`¿Eliminar ${selectedIngredients.length} ingrediente(s)?`)) return;
    setDeleting(true);
    let deleted = 0;
    for (const id of selectedIngredients) {
      try { await api.deleteIngredient(id); deleted++; } catch (error) { console.error("Error deleting ingredient:", id, error); }
    }
    setIngredients(prev => prev.filter(i => !selectedIngredients.includes(i.ingredient_id)));
    setSelectedIngredients([]);
    setDeleting(false);
    toast.success(`${deleted} ingrediente(s) eliminados`);
  };

  const openMergeDialog = () => {
    const [idA, idB] = selectedIngredients;
    const ingA = ingredients.find(i => i.ingredient_id === idA);
    const ingB = ingredients.find(i => i.ingredient_id === idB);
    if (!ingA || !ingB) return;

    let stockA = ingA.current_stock, stockB = ingB.current_stock;
    let costA = ingA.cost_per_unit, costB = ingB.cost_per_unit;
    let targetUnit = ingA.unit, convNote = null;

    if (ingA.unit !== ingB.unit) {
      const normA = toBase(ingA), normB = toBase(ingB);
      if (normA && normB && normA.base === normB.base) {
        stockA = normA.stock; stockB = normB.stock; costA = normA.cost; costB = normB.cost; targetUnit = normA.base;
        convNote = `${ingA.current_stock} ${ingA.unit} + ${ingB.current_stock} ${ingB.unit} → convertido a ${targetUnit}`;
      } else {
        convNote = `Unidades incompatibles (${ingA.unit} y ${ingB.unit}). Elige la unidad manualmente.`;
      }
    }

    const totalStock = stockA + stockB;
    const weightedCost = totalStock > 0 ? (costA * stockA + costB * stockB) / totalStock : (costA + costB) / 2;
    const base = stockA >= stockB ? ingA : ingB;

    setMergePair([ingA, ingB]);
    setMergeNormalized({ stockA, stockB, costA, costB, unit: targetUnit, note: convNote });
    setMergeForm({
      name: base.name, unit: targetUnit, cost_per_unit: weightedCost.toFixed(4),
      waste_percentage: ((ingA.waste_percentage + ingB.waste_percentage) / 2).toFixed(1),
      min_stock: Math.max(ingA.min_stock, ingB.min_stock).toString(), supplier_id: base.supplier_id || "",
    });
    setMergeDialogOpen(true);
  };

  const handleMerge = async () => {
    if (!mergePair) return;
    const [ingA, ingB] = mergePair;
    const totalStock = mergeNormalized ? mergeNormalized.stockA + mergeNormalized.stockB : ingA.current_stock + ingB.current_stock;
    setSaving(true);
    try {
      await api.updateIngredient(ingA.ingredient_id, {
        name: mergeForm.name, unit: mergeForm.unit, cost_per_unit: parseFloat(mergeForm.cost_per_unit),
        waste_percentage: parseFloat(mergeForm.waste_percentage), supplier_id: mergeForm.supplier_id || "",
        min_stock: parseFloat(mergeForm.min_stock) || 0, current_stock: totalStock,
      });
      await api.deleteIngredient(ingB.ingredient_id);
      setIngredients(prev => prev.filter(i => i.ingredient_id !== ingB.ingredient_id).map(i =>
        i.ingredient_id === ingA.ingredient_id ? { ...i, ...mergeForm, cost_per_unit: parseFloat(mergeForm.cost_per_unit), waste_percentage: parseFloat(mergeForm.waste_percentage), min_stock: parseFloat(mergeForm.min_stock) || 0, current_stock: totalStock } : i
      ));
      setSelectedIngredients([]); setMergeDialogOpen(false); setMergePair(null); setMergeNormalized(null);
      toast.success(`Fusionados en "${mergeForm.name}" · stock: ${totalStock} ${mergeForm.unit}`);
    } catch (error) { toast.error("Error al fusionar: " + error.message); }
    finally { setSaving(false); }
  };

  const processInvoiceFile = async (file) => {
    if (!file) return;
    // Keep the raw file around so we can persist it to Accounting later
    setInvoiceFile(file);
    setImportingInvoice(true); setInvoiceData(null); setSelectedItems([]); setItemMappings({}); setReconcileData(null); setImportDialogOpen(true);
    try {
      const result = await api.aiExtractInvoice(file, restaurantId);
      if (result.success && result.data) {
        setInvoiceData(result.data);
        const allIndices = result.data.items?.map((_, i) => i) || [];
        setSelectedItems(allIndices);
        const mappings = {};
        result.data.items?.forEach((item, i) => {
          const match = findBestMatch(item.name, ingredients);
          mappings[i] = match ? { mode: 'existing', ingredientId: match.ingredient_id } : { mode: 'new', ingredientId: null };
        });
        setItemMappings(mappings);
        toast.success("Factura procesada correctamente");

        // Reconcile against pending shopping lists (fire-and-forget)
        api.reconcileInvoiceWithOrders(restaurantId, result.data)
          .then((rc) => setReconcileData(rc))
          .catch((err) => { console.warn("Reconcile failed:", err); });
      } else toast.error("No se pudieron extraer datos de la factura");
    } catch (error) { toast.error("Error al procesar factura: " + error.message); }
    finally { setImportingInvoice(false); if (invoiceFileRef.current) invoiceFileRef.current.value = ""; }
  };

  const handleInvoiceUpload = async (event) => {
    const file = event.target.files?.[0];
    await processInvoiceFile(file);
  };

  const importSelectedItems = async () => {
    if (!invoiceData?.items || selectedItems.length === 0) { toast.error("Selecciona al menos un producto"); return; }
    setSaving(true);
    let imported = 0, updated = 0;
    const newItems = selectedItems.map(i => ({ index: i, item: invoiceData.items[i] })).filter(({ index }) => itemMappings[index]?.mode !== 'existing');
    const wasteMap = {};
    if (newItems.length > 0) {
      setImportProgress(`Calculando merma con IA para ${newItems.length} nuevos ingredientes...`);
      await Promise.all(newItems.map(async ({ index, item }) => {
        try { const r = await api.aiEstimateWaste(item.name, "", restaurantId); wasteMap[index] = r.waste_percentage; }
        catch { wasteMap[index] = 10; }
      }));
    }
    setImportProgress("Guardando ingredientes...");
    const priceItems = []; // for price-history recording
    for (const index of selectedItems) {
      const item = invoiceData.items[index]; if (!item?.name) continue;
      const mapping = itemMappings[index];
      try {
        if (mapping?.mode === 'existing' && mapping.ingredientId) {
          const existing = ingredients.find(i => i.ingredient_id === mapping.ingredientId);
          if (existing) {
            await api.updateIngredient(existing.ingredient_id, {
              name: existing.name, unit: existing.unit, cost_per_unit: item.unit_price ?? existing.cost_per_unit,
              waste_percentage: existing.waste_percentage, supplier_id: existing.supplier_id || "",
              min_stock: existing.min_stock, current_stock: existing.current_stock + (item.quantity || 0)
            }); updated++;
            if (item.unit_price) priceItems.push({ ingredient_id: existing.ingredient_id, unit_price: item.unit_price, quantity: item.quantity, unit: item.unit });
          }
        } else {
          const created = await api.createIngredient(currentRestaurant.restaurant_id, {
            name: item.name, unit: item.unit || "kg", cost_per_unit: item.unit_price || 0,
            waste_percentage: wasteMap[index] ?? 10, current_stock: item.quantity || 0, min_stock: 0
          }); imported++;
          if (item.unit_price && created?.ingredient_id) priceItems.push({ ingredient_id: created.ingredient_id, unit_price: item.unit_price, quantity: item.quantity, unit: item.unit });
        }
      } catch (error) { console.error("Error importing item:", item.name, error); }
    }

    // Record price history snapshots (fire-and-forget)
    if (priceItems.length > 0) {
      api.recordIngredientPrices(currentRestaurant.restaurant_id, {
        supplier_id: invoiceData.supplier_id || null,
        supplier_name: invoiceData.supplier_name || null,
        invoice_date: invoiceData.invoice_date || null,
        source: "invoice",
        items: priceItems,
      }).catch((e) => console.warn("Price snapshots failed:", e));
    }

    // Integration with Accounting: log the invoice as a purchase expense so the P&L,
    // cash-flow, and export-for-accountant flows include it automatically.
    //
    // Preferred path: send the raw file + structured data to the backend so we also
    // persist a `finance_document` (appears in Bandeja + monthly export ZIP).
    // Fallback: if the file isn't available (e.g. manual re-import) just create
    // the expense row so at least the P&L is correct.
    const invoiceTotal = parseFloat(invoiceData.total ?? invoiceData.total_amount ?? 0);
    let expenseLogged = false;
    let documentLinked = false;
    if (invoiceTotal > 0) {
      if (invoiceFile) {
        try {
          await api.createDocumentFromInventory(
            currentRestaurant.restaurant_id,
            invoiceFile,
            { ...invoiceData, items_count: selectedItems.length },
          );
          expenseLogged = true;
          documentLinked = true;
        } catch (e) {
          if (e?.code === "duplicate_document") {
            // Ask the user whether to force-save the duplicate finance document.
            const confirmed = await new Promise((resolve) => {
              setPendingDupInventory({ duplicate: e.duplicate, resolve });
            });
            if (confirmed) {
              try {
                await api.createDocumentFromInventory(
                  currentRestaurant.restaurant_id,
                  invoiceFile,
                  { ...invoiceData, items_count: selectedItems.length },
                  { force: true },
                );
                expenseLogged = true;
                documentLinked = true;
              } catch (e2) {
                console.warn("Duplicate retry failed:", e2);
              }
            } else {
              toast.info("Gasto no duplicado en Contabilidad.");
              // Mark as handled so we don't create a second expense via fallback
              expenseLogged = true;
            }
          } else {
            console.warn("Document persistence failed, falling back to expense-only:", e);
          }
        }
      }
      if (!expenseLogged) {
        try {
          await api.createExpense(currentRestaurant.restaurant_id, {
            date: invoiceData.invoice_date || new Date().toISOString().slice(0, 10),
            amount: invoiceTotal,
            category: "purchases",
            supplier_name: invoiceData.supplier_name || null,
            supplier_id: invoiceData.supplier_id || null,
            tax_amount: parseFloat(invoiceData.tax_amount ?? invoiceData.iva ?? 0) || null,
            notes: `Importado desde Inventario · ${selectedItems.length} productos`,
            paid: false,
          }, { force: true }); // already handled dup-check at document level above
          expenseLogged = true;
        } catch (e) {
          console.warn("Expense logging to Accounting failed (non-blocking):", e);
        }
      }
    }

    setSaving(false); setImportProgress(""); setImportDialogOpen(false); setInvoiceData(null); setInvoiceFile(null);
    await fetchData();
    const parts = [];
    if (imported) parts.push(`${imported} nuevos creados`);
    if (updated) parts.push(`${updated} actualizados`);
    if (documentLinked) parts.push(`factura ${formatCurrency(invoiceTotal)} archivada en Contabilidad`);
    else if (expenseLogged) parts.push(`gasto ${formatCurrency(invoiceTotal)} registrado en Contabilidad`);
    toast.success(parts.join(" · ") || "Importación completada");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.cost_per_unit) { toast.error("Nombre y coste son requeridos"); return; }
    setSaving(true);
    try {
      const data = {
        ...formData, cost_per_unit: parseFloat(formData.cost_per_unit),
        waste_percentage: parseFloat(formData.waste_percentage),
        min_stock: parseFloat(formData.min_stock), current_stock: parseFloat(formData.current_stock)
      };
      if (editingIngredient) {
        const u = await api.updateIngredient(editingIngredient.ingredient_id, data);
        setIngredients(ingredients.map(i => i.ingredient_id === editingIngredient.ingredient_id ? u : i));
        toast.success("Ingrediente actualizado");
      } else {
        const n = await api.createIngredient(currentRestaurant.restaurant_id, data);
        setIngredients([...ingredients, n]); toast.success("Ingrediente creado");
      }
      closeDialog();
    } catch (error) { toast.error(error.message || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const closeDialog = () => {
    setDialogOpen(false); setEditingIngredient(null);
    setFormData({ name: "", unit: "kg", cost_per_unit: "", waste_percentage: "0", supplier_id: "", min_stock: "0", current_stock: "0", image_url: "" });
  };

  const openEdit = (ingredient) => {
    setEditingIngredient(ingredient);
    setFormData({
      name: ingredient.name, unit: ingredient.unit, cost_per_unit: ingredient.cost_per_unit.toString(),
      waste_percentage: ingredient.waste_percentage.toString(), supplier_id: ingredient.supplier_id || "",
      min_stock: ingredient.min_stock.toString(), current_stock: ingredient.current_stock.toString(), image_url: ingredient.image_url || ""
    });
    setDialogOpen(true);
  };

  const isLowStock = (ing) => ing.current_stock < ing.min_stock;
  const getEffectiveCost = (ing) => ing.cost_per_unit * (1 + ing.waste_percentage / 100);

  // ─── Memos ────────────────────────────────────────────────────────

  const filteredAndSortedIngredients = useMemo(() => {
    let result = [...ingredients];
    if (searchQuery.trim()) { const q = searchQuery.toLowerCase(); result = result.filter(ing => ing.name?.toLowerCase().includes(q)); }
    if (showLowStockOnly) result = result.filter(ing => ing.current_stock < ing.min_stock);
    result.sort((a, b) => {
      switch (sortBy) {
        case "name": return (a.name || "").localeCompare(b.name || "", 'es');
        case "name_desc": return (b.name || "").localeCompare(a.name || "", 'es');
        case "stock_low": return (a.current_stock - a.min_stock) - (b.current_stock - b.min_stock);
        case "stock_high": return (b.current_stock - b.min_stock) - (a.current_stock - a.min_stock);
        case "cost": return b.cost_per_unit - a.cost_per_unit;
        case "cost_asc": return a.cost_per_unit - b.cost_per_unit;
        default: return 0;
      }
    });
    return result;
  }, [ingredients, searchQuery, sortBy, showLowStockOnly]);

  const lowStockCount = ingredients.filter(isLowStock).length;

  // ─── Render ───────────────────────────────────────────────────────

  if (loading) return <PageSkeleton kpis={4} showTabs rows={5} />;

  return (
    <div className="space-y-6" data-testid="inventory-page">
      {/* Header */}
      <PageHeader
        icon={<Package className="w-5 h-5" />}
        title="Inventario"
        description="Gestiona ingredientes, stock y costes."
        breadcrumb={[{ label: "Operaciones" }, { label: "Inventario" }]}
        actions={
          lowStockCount > 0 && (
            <Badge variant="destructive" className="gap-1" data-testid="low-stock-header-badge">
              <AlertTriangle className="w-3 h-3" /> {lowStockCount} bajo stock
            </Badge>
          )
        }
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList data-testid="inventory-tabs">
          <TabsTrigger value="dashboard" className="gap-1.5" data-testid="inventory-tab-dashboard">
            <LayoutDashboard className="w-4 h-4" /> Resumen
          </TabsTrigger>
          <TabsTrigger value="contar" className="gap-1.5" data-testid="inventory-tab-contar">
            <ClipboardList className="w-4 h-4" /> Contar
          </TabsTrigger>
          <TabsTrigger value="productos" className="gap-1.5" data-testid="inventory-tab-productos">
            <Boxes className="w-4 h-4" /> Productos
            <Badge variant="secondary" className="ml-1 text-[10px] px-1.5 py-0 h-4">{ingredients.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="reportes" className="gap-1.5" data-testid="inventory-tab-reportes">
            <FileBarChart className="w-4 h-4" /> Reportes
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-4">
          <InventoryDashboard
            key={dashboardKey}
            restaurantId={currentRestaurant?.restaurant_id}
            onGoToProducts={() => setActiveTab("productos")}
            onOpenPurchases={() => navigate("/app/shopping-lists")}
            onImportInvoice={() => setCameraOpen(true)}
            onImportCsv={() => setCsvImportOpen(true)}
            onGoToCount={() => setActiveTab("contar")}
            onRegisterWaste={(iid) => { setWastePreselectedId(iid || null); setWasteOpen(true); }}
          />
        </TabsContent>

        <TabsContent value="contar" className="mt-4">
          <InventoryCountView
            restaurantId={currentRestaurant?.restaurant_id}
            onBack={() => setActiveTab("dashboard")}
          />
        </TabsContent>

        <TabsContent value="reportes" className="mt-4">
          <InventoryReports restaurantId={currentRestaurant?.restaurant_id} />
        </TabsContent>

        <TabsContent value="productos" className="mt-4 space-y-6">
          {/* Actions */}
          <div className="flex flex-wrap items-center gap-3 justify-end">
            {selectedIngredients.length > 0 && (
              <>
                {selectedIngredients.length === 2 && (
                  <Button variant="outline" onClick={openMergeDialog} className="border-primary text-primary hover:bg-primary/10" data-testid="merge-ingredients-btn">
                    <ArrowUpDown className="w-4 h-4 mr-2" /> Fusionar (2)
                  </Button>
                )}
                <Button variant="destructive" onClick={handleDeleteSelected} disabled={deleting} data-testid="delete-selected-btn">
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />} Eliminar ({selectedIngredients.length})
                </Button>
              </>
            )}
            <input type="file" ref={invoiceFileRef} onChange={handleInvoiceUpload} accept="image/*,.pdf" className="hidden" />
            <Button
              onClick={() => setQuickAddOpen(true)}
              className="gap-1.5"
              data-testid="quick-add-ingredient-btn"
            >
              <Sparkles className="w-4 h-4" /> Alta rápida con IA
            </Button>
            <Button
              onClick={() => setWasteOpen(true)}
              variant="outline"
              className="gap-1.5 border-destructive/40 text-destructive hover:bg-destructive/10"
              data-testid="register-waste-btn"
            >
              <Trash2 className="w-4 h-4" /> Registrar merma
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="import-invoice-btn" className="gap-1.5">
                  <Camera className="w-4 h-4" /> Importar factura <ChevronDown className="w-3 h-3 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onClick={() => setCameraOpen(true)} className="gap-2 cursor-pointer" data-testid="import-invoice-camera">
                  <Camera className="w-4 h-4" />
                  <span>Hacer foto ahora</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => invoiceFileRef.current?.click()} className="gap-2 cursor-pointer" data-testid="import-invoice-file">
                  <UploadIcon className="w-4 h-4" />
                  <span>Subir archivo</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" onClick={() => setCsvImportOpen(true)} data-testid="import-csv-btn">
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Importar CSV
            </Button>
            <Button variant="outline" onClick={handleReconcile} disabled={reconciling} data-testid="reconcile-btn" title="Busca ingredientes mencionados en tus recetas que no están dados de alta y los crea automáticamente">
              {reconciling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-2" />} Sincronizar con recetas
            </Button>
            <IngredientDialog
              open={dialogOpen} onOpenChange={setDialogOpen} editingIngredient={editingIngredient}
              formData={formData} setFormData={setFormData} onSubmit={handleSubmit} saving={saving}
              suppliers={suppliers} closeDialog={closeDialog} restaurantId={currentRestaurant?.restaurant_id}
            />
          </div>

          {/* Search and Filter */}
          <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Buscar ingrediente..." value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" data-testid="inventory-search-input" />
            </div>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full sm:w-[200px]" data-testid="inventory-sort-select" aria-label="Ordenar inventario">
                <ArrowUpDown className="w-4 h-4 mr-2" /><SelectValue placeholder="Ordenar por..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">A-Z (Alfabetico)</SelectItem>
                <SelectItem value="name_desc">Z-A (Alfabetico inverso)</SelectItem>
                <SelectItem value="stock_low">Stock mas bajo primero</SelectItem>
                <SelectItem value="stock_high">Stock mas alto primero</SelectItem>
                <SelectItem value="cost">Precio mayor primero</SelectItem>
                <SelectItem value="cost_asc">Precio menor primero</SelectItem>
              </SelectContent>
            </Select>
            <Button variant={showLowStockOnly ? "default" : "outline"} onClick={() => setShowLowStockOnly(!showLowStockOnly)}
              className="gap-2" data-testid="low-stock-filter-btn">
              <AlertTriangle className="w-4 h-4" /> {showLowStockOnly ? "Mostrando bajo stock" : "Solo bajo stock"}
              {lowStockCount > 0 && !showLowStockOnly && <Badge variant="destructive" className="ml-1">{lowStockCount}</Badge>}
            </Button>
          </div>
          <div className="mt-3 text-sm text-muted-foreground">
            {filteredAndSortedIngredients.length === ingredients.length
              ? <span>{ingredients.length} ingredientes en total</span>
              : <span>Mostrando {filteredAndSortedIngredients.length} de {ingredients.length} ingredientes</span>}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {ingredients.length === 0 ? (
        <Card className="border-dashed border-2">
          <CardContent className="py-16 text-center">
            <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="font-heading text-lg font-semibold mb-2">Sin ingredientes</h3>
            <p className="text-muted-foreground mb-6">Añade ingredientes manualmente o importa desde una factura</p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => setCameraOpen(true)}><Camera className="w-4 h-4 mr-2" /> Hacer foto</Button>
              <Button variant="outline" onClick={() => invoiceFileRef.current?.click()}><UploadIcon className="w-4 h-4 mr-2" /> Subir archivo</Button>
              <Button onClick={() => setDialogOpen(true)}><Plus className="w-4 h-4 mr-2" /> Añadir ingrediente</Button>
            </div>
          </CardContent>
        </Card>
      ) : filteredAndSortedIngredients.length === 0 ? (
        <Card className="border-dashed border-2">
          <CardContent className="py-16 text-center">
            <Search className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="font-heading text-lg font-semibold mb-2">Sin resultados</h3>
            <p className="text-muted-foreground mb-4">No se encontraron ingredientes que coincidan con tu busqueda</p>
            <Button variant="outline" onClick={() => { setSearchQuery(""); setShowLowStockOnly(false); }}>Limpiar filtros</Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-none shadow-soft">
          <CardContent className="p-0 overflow-x-auto">
            <Table className="min-w-[640px]">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox checked={selectedIngredients.length === filteredAndSortedIngredients.length && filteredAndSortedIngredients.length > 0}
                      onCheckedChange={(c) => setSelectedIngredients(c ? filteredAndSortedIngredients.map(i => i.ingredient_id) : [])}
                      data-testid="select-all-ingredients" />
                  </TableHead>
                  <TableHead>Ingrediente</TableHead>
                  <TableHead>Coste</TableHead>
                  <TableHead>Merma</TableHead>
                  <TableHead>Coste efectivo</TableHead>
                  <TableHead>Stock</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAndSortedIngredients.map((ingredient) => (
                  <TableRow key={ingredient.ingredient_id} data-testid={`ingredient-row-${ingredient.ingredient_id}`}
                    className={selectedIngredients.includes(ingredient.ingredient_id) ? "bg-destructive/5" : ""}>
                    <TableCell>
                      <Checkbox checked={selectedIngredients.includes(ingredient.ingredient_id)}
                        onCheckedChange={(c) => setSelectedIngredients(prev => c ? [...prev, ingredient.ingredient_id] : prev.filter(id => id !== ingredient.ingredient_id))}
                        data-testid={`select-ingredient-${ingredient.ingredient_id}`} />
                    </TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <LazyIngredientImage ingredient={ingredient} className="w-8 h-8 rounded-md flex-shrink-0 border border-border" />
                        {ingredient.name}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{formatCurrency(ingredient.cost_per_unit)}/{ingredient.unit}</TableCell>
                    <TableCell>{ingredient.waste_percentage}%</TableCell>
                    <TableCell className="font-mono text-sm text-primary">{formatCurrency(getEffectiveCost(ingredient))}/{ingredient.unit}</TableCell>
                    <TableCell>
                      <span className={isLowStock(ingredient) ? "text-destructive font-medium" : ""}>{ingredient.current_stock}</span>
                      <span className="text-muted-foreground"> / {ingredient.min_stock} {ingredient.unit}</span>
                    </TableCell>
                    <TableCell>
                      {isLowStock(ingredient)
                        ? <Badge variant="destructive" className="gap-1"><AlertTriangle className="w-3 h-3" /> Bajo</Badge>
                        : <Badge variant="secondary" className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">OK</Badge>}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(ingredient)}
                        data-testid={`edit-ingredient-btn-${ingredient.ingredient_id}`}><Edit className="w-4 h-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <CameraCaptureDialog
        open={cameraOpen}
        onOpenChange={setCameraOpen}
        onCapture={(file) => { processInvoiceFile(file); }}
      />
      <InvoiceImportDialog
        open={importDialogOpen} onOpenChange={setImportDialogOpen} importingInvoice={importingInvoice}
        saving={saving} invoiceData={invoiceData} selectedItems={selectedItems} setSelectedItems={setSelectedItems}
        itemMappings={itemMappings} setItemMappings={setItemMappings} importProgress={importProgress}
        ingredients={ingredients} onImport={importSelectedItems}
        reconcileData={reconcileData}
      />
      <MergeDialog
        open={mergeDialogOpen} onOpenChange={setMergeDialogOpen} mergePair={mergePair}
        mergeNormalized={mergeNormalized} mergeForm={mergeForm} setMergeForm={setMergeForm}
        onMerge={handleMerge} saving={saving}
      />
      <CsvImportDialog
        open={csvImportOpen} onOpenChange={setCsvImportOpen}
        restaurantId={currentRestaurant?.restaurant_id} onImported={fetchData}
      />
      <QuickAddIngredientDialog
        open={quickAddOpen}
        onOpenChange={setQuickAddOpen}
        restaurantId={currentRestaurant?.restaurant_id}
        onCreated={() => { fetchData(); }}
      />
      <WasteDialog
        open={wasteOpen}
        onOpenChange={(v) => { setWasteOpen(v); if (!v) setWastePreselectedId(null); }}
        restaurantId={currentRestaurant?.restaurant_id}
        preselectedIngredientId={wastePreselectedId}
        onSaved={() => { fetchData(); }}
      />
      <ReconcileResultBanner result={reconcileResult} onDismiss={() => setReconcileResult(null)} />

      {pendingDupInventory && (
        <AlertDialog
          open={!!pendingDupInventory}
          onOpenChange={(open) => {
            if (!open && pendingDupInventory) {
              pendingDupInventory.resolve(false);
              setPendingDupInventory(null);
            }
          }}
        >
          <AlertDialogContent data-testid="inventory-duplicate-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>⚠️ Parece que ya tenías esta factura cargada</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-2 text-sm">
                  <p>¿Seguro que quieres añadirla en Contabilidad? Se podría duplicar el gasto en tu P&amp;L.</p>
                  {pendingDupInventory.duplicate && (
                    <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-amber-900">
                      <div className="font-medium">
                        {pendingDupInventory.duplicate.supplier_name || pendingDupInventory.duplicate.filename || "Documento existente"}
                      </div>
                      <div className="text-xs mt-1 space-y-0.5">
                        {pendingDupInventory.duplicate.total_amount != null && (
                          <div>Importe: <strong>{formatCurrency(pendingDupInventory.duplicate.total_amount)}</strong></div>
                        )}
                        {pendingDupInventory.duplicate.issue_date && (
                          <div>Fecha: <strong>{pendingDupInventory.duplicate.issue_date}</strong></div>
                        )}
                        {pendingDupInventory.duplicate.invoice_number && (
                          <div>Nº factura: <strong>{pendingDupInventory.duplicate.invoice_number}</strong></div>
                        )}
                        {pendingDupInventory.duplicate.reason && (
                          <div className="italic opacity-80">{pendingDupInventory.duplicate.reason}</div>
                        )}
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Los productos del inventario ya se han guardado — esto solo afecta a la factura en Contabilidad.
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                data-testid="inventory-duplicate-cancel-btn"
                onClick={() => {
                  pendingDupInventory.resolve(false);
                  setPendingDupInventory(null);
                }}
              >
                No, cancelar
              </AlertDialogCancel>
              <AlertDialogAction
                data-testid="inventory-duplicate-confirm-btn"
                className="bg-amber-600 hover:bg-amber-700"
                onClick={(e) => {
                  e.preventDefault();
                  pendingDupInventory.resolve(true);
                  setPendingDupInventory(null);
                }}
              >
                Sí, añadir igualmente
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
